import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "askButton",
    "stopButton",
    "phase",
    "warning",
    "history",
    "emptyState",
    "answer",
    "sources",
    "feedback"
  ]
  static values = {
    streamUrl: String,
    feedbackUrl: String,
    query: String,
    type: String,
    showConfidence: Boolean
  }

  connect() {
    this.abortController = null
    this.lastAnswerText = ""
    this.answerForFeedback = ""
    this.lastSubmittedQuery = ""
    this.conversationMessages = []
    /** The <div> inside the current AI bubble where tokens stream into. */
    this.currentAiTextEl = null
    /** The inner wrapper of the current AI bubble turn (text + sources). */
    this.currentAiTurnEl = null
    /** Sources received via meta event, held until done. */
    this.pendingSources = []
  }

  resize() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }

  onKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.ask()
    }
  }

  stop() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.setLoading(false)
    this.phaseTarget.textContent = "Stopped."
  }

  async ask() {
    const userTurn = (this.inputTarget.value || "").trim()
    if (!userTurn.length) return

    this.lastSubmittedQuery = userTurn

    this.stop()
    this.abortController = new AbortController()
    this.phaseTarget.textContent = ""
    this.warningTarget.classList.add("hidden")
    this.feedbackTarget.classList.add("hidden")
    this.feedbackTarget.classList.remove("opacity-50")
    this.lastAnswerText = ""
    this.pendingSources = []
    this.currentAiTextEl = null
    this.currentAiTurnEl = null

    this.hideEmptyState()
    this.appendUserBubble(userTurn)
    this.inputTarget.value = ""
    this.resize()

    this.setLoading(true)

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const body = new URLSearchParams()
    body.set("authenticity_token", token || "")
    body.set("q", userTurn)
    if (this.typeValue) body.set("type", this.typeValue)
    if (this.conversationMessages.length) {
      body.set("messages", JSON.stringify(this.conversationMessages))
    }

    try {
      const res = await fetch(this.streamUrlValue, {
        method: "POST",
        body,
        signal: this.abortController.signal,
        headers: {
          Accept: "text/event-stream",
          "X-CSRF-Token": token || ""
        },
        credentials: "same-origin"
      })

      if (!res.ok) {
        this.appendSystemNotice("Could not start answer stream.")
        return
      }

      await this.parseStream(res)
    } catch (e) {
      if (e.name === "AbortError") return
      this.appendSystemNotice("Something went wrong.")
    } finally {
      this.setLoading(false)
    }
  }

  async parseStream(res) {
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let idx
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, idx)
        buffer = buffer.slice(idx + 2)
        this.processSseBlock(block)
      }
    }
    if (buffer.trim()) this.processSseBlock(buffer)
  }

  processSseBlock(block) {
    if (!block.trim()) return
    let eventName = "message"
    const dataLines = []
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) eventName = line.slice(6).trim()
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return
    let data
    try {
      data = JSON.parse(dataLines.join("\n"))
    } catch {
      return
    }

    if (eventName === "phase") {
      if (data.message) this.phaseTarget.textContent = data.message
    } else if (eventName === "meta") {
      this.applyMeta(data)
    } else if (eventName === "token") {
      const t = data.t || ""
      this.lastAnswerText += t
      if (!this.currentAiTextEl) this.startAiBubble()
      this.currentAiTextEl.textContent += t
      this.historyTarget.scrollTop = this.historyTarget.scrollHeight
    } else if (eventName === "done") {
      this.phaseTarget.textContent = ""
      if (this.lastAnswerText.length) {
        if (this.pendingSources.length && this.currentAiTurnEl) {
          this.appendSourcesToTurn(this.currentAiTurnEl, this.pendingSources)
        }
        this.answerForFeedback = this.lastAnswerText
        this.feedbackTarget.classList.remove("hidden")
        this.feedbackTarget.classList.add("flex")
        this.conversationMessages.push({ role: "user", content: this.lastSubmittedQuery })
        this.conversationMessages.push({ role: "assistant", content: this.lastAnswerText })
      }
      this.currentAiTextEl = null
      this.currentAiTurnEl = null
      this.pendingSources = []
      this.historyTarget.scrollTop = this.historyTarget.scrollHeight
    } else if (eventName === "error") {
      this.currentAiTextEl = null
      this.currentAiTurnEl = null
      this.appendSystemNotice(data.message || "Error")
    }
  }

  applyMeta(data) {
    const sources = data.sources || []
    const warning = data.warning
    this.pendingSources = sources

    if (warning === "no_results") {
      this.startAiBubble()
      this.currentAiTextEl.textContent =
        "No indexed documents matched closely enough to ground an answer. Try different keywords or browse the results list."
      this.currentAiTextEl.classList.add("text-amber-800")
      this.phaseTarget.textContent = ""
      return
    }

    if (warning === "low_confidence") {
      this.warningTarget.classList.remove("hidden")
      this.warningTarget.textContent =
        "Best match is weak; the answer may be less reliable. Verify against the sources below."
    } else {
      this.warningTarget.classList.add("hidden")
    }
  }

  /** Create an AI bubble in the conversation and save refs for streaming. */
  startAiBubble() {
    const row = document.createElement("div")
    row.className = "flex justify-start items-end gap-2"

    const avatar = document.createElement("div")
    avatar.className =
      "shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold self-end mb-0.5"
    avatar.textContent = "AI"

    const inner = document.createElement("div")
    inner.className = "max-w-[85%] space-y-2"

    const textEl = document.createElement("div")
    textEl.className =
      "bg-gray-100 text-gray-800 text-sm rounded-2xl rounded-bl-sm px-4 py-2.5 leading-relaxed whitespace-pre-wrap"

    inner.appendChild(textEl)
    row.appendChild(avatar)
    row.appendChild(inner)
    this.historyTarget.appendChild(row)

    this.currentAiTextEl = textEl
    this.currentAiTurnEl = inner
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight
  }

  appendUserBubble(text) {
    const row = document.createElement("div")
    row.className = "flex justify-end items-end gap-2"

    const bubble = document.createElement("div")
    bubble.className =
      "max-w-[80%] bg-violet-600 text-white text-sm rounded-2xl rounded-br-sm px-4 py-2.5 leading-relaxed"
    bubble.textContent = text

    row.appendChild(bubble)
    this.historyTarget.appendChild(row)
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight
  }

  appendSourcesToTurn(container, sources) {
    const showDist = this.showConfidenceValue
    const wrapper = document.createElement("div")
    wrapper.className = "flex flex-col gap-1.5"

    wrapper.innerHTML = sources
      .map((s) => {
        const label = this.escapeHtml(s.title || "Source")
        const href = typeof s.url === "string" && s.url.startsWith("/") ? s.url : "#"
        const dist =
          showDist && s.distance != null && s.distance !== ""
            ? `<span class="text-gray-400 ml-1">${this.escapeHtml(String(Number(s.distance).toFixed(4)))}</span>`
            : ""
        const num = s.index != null
          ? `<span class="text-violet-600 font-mono mr-1">[${s.index}]</span>`
          : ""
        const gistRaw = (s.gist && String(s.gist).trim()) || ""
        const gistBlock = gistRaw
          ? `<p class="mt-1 text-xs text-gray-500 leading-snug line-clamp-3 break-words">${this.escapeHtml(gistRaw)}</p>`
          : ""
        return `<div class="rounded-xl border border-violet-100 bg-violet-50/60 px-3 py-2 hover:bg-violet-100/60 transition-colors">
          <a href="${href}" class="inline-flex flex-wrap items-baseline gap-x-1 text-xs font-medium text-violet-800 hover:text-violet-950 max-w-full">
            <span class="truncate">${num}${label}${dist}</span>
          </a>
          ${gistBlock}
        </div>`
      })
      .join("")

    container.appendChild(wrapper)
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight
  }

  appendSystemNotice(message) {
    const el = document.createElement("div")
    el.className =
      "text-xs text-center text-gray-400 italic py-1"
    el.textContent = message
    this.historyTarget.appendChild(el)
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }
  }

  voteUp() {
    this.sendFeedback(1)
  }

  voteDown() {
    this.sendFeedback(-1)
  }

  async sendFeedback(vote) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const body = new URLSearchParams()
    body.set("authenticity_token", token || "")
    body.set("q", this.lastSubmittedQuery || (this.inputTarget.value || "").trim())
    body.set("vote", String(vote))
    const digestSource = this.answerForFeedback || this.lastAnswerText
    if (digestSource.length) {
      const digest = await this.digestText(digestSource)
      body.set("answer_digest", digest)
    }
    try {
      await fetch(this.feedbackUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": token || "" },
        credentials: "same-origin"
      })
      this.feedbackTarget.classList.add("opacity-50")
    } catch (_) {
      /* ignore */
    }
  }

  async digestText(text) {
    const enc = new TextEncoder().encode(text)
    const buf = await crypto.subtle.digest("SHA-256", enc)
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32)
  }

  escapeHtml(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  setLoading(loading) {
    this.askButtonTarget.disabled = loading
    this.stopButtonTarget.classList.toggle("hidden", !loading)
  }
}
